import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, model, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Complaint, ComplaintService, UpdateComplaintVo } from '../../../core/services/complaint.service';
import { LookupItem } from '../../../core/services/lookup.service';
import { MatDialog } from '@angular/material/dialog';
import { AuthContext } from '../../../auth/services/auth.context';
import { PopupDialogComponent } from '../../../components/popup-dialog/popup-dialog.component';

@Component({
  selector: 'app-user-dashboard',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatCardModule,
    CommonModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './user-dashboard.component.html',
  styleUrl: './user-dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserDashboardComponent implements OnInit, AfterViewInit {

  private snackBar = inject(MatSnackBar);

  displayedColumns: string[] = ['complaintId', 'complaintDescription', 'complaintDate', 'complaintStatus', 'departmentName', 'employeeName', 'employeeMobileNumber', 'closeComplaint'];
  dataSource!: MatTableDataSource<Complaint>;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  userComplaints: Complaint[] = [];

  complaintLineStatusType: LookupItem[] = [];

  readonly event = model('');
  readonly dialog = inject(MatDialog);

  constructor(
    private complaintService: ComplaintService,
    private authContext: AuthContext,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.getUserComplaints();
    this.authContext.lookUpValue$.subscribe(lookups => {
      if (lookups) {
        this.complaintLineStatusType = lookups['COMPLAINT_LINE_STATUS_TYPE'] || [];
        this.cdr.detectChanges();
      }
    });
  }

  ngAfterViewInit() {
    if (this.dataSource) {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
    }
    this.cdr.detectChanges();  // Force a re-render after the view initializes
  }

  openDialog(complaint: Complaint): void {
    let message;
    if (complaint.complaintStatus === 'CLOSED') {
      message = 'Are you sure to open your complaint?';
    } else {
      message = 'Are you sure to close your complaint?';
    }
    const dialogRef = this.dialog.open(PopupDialogComponent, {
      data: { complaintId: complaint.complaintId, event: complaint, message: message, title: 'Complaint No - ' + complaint.complaintId },
      width: '300px',
      height: '200px'
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result !== undefined) {
        this.event.set(result);
        // console.log(result);
        const updateComplaint: UpdateComplaintVo = {
          complaintId: complaint.complaintId,
          complaintLineId: complaint.complaintLineId,
          status: complaint.complaintStatus === 'CLOSED' ? 'REOPEN' : 'CLOSED'
        }

        this.complaintService.updateComplaint(updateComplaint).subscribe({
          next: (response) => {
            if (response) {
              // console.log(response);
              this.openSnackBar(response.message, "Okay");
              this.getUserComplaints();
            }
          },
          error: (error) => {
            console.error('POST error:', error);
            this.openSnackBar("Bad Request", "Okay");
          },
        });
      }
    });
  }

  openSnackBar(message: string, action?: string) {
    this.snackBar.open(message, action, { duration: 3 * 1000 }); // 3 seconds
  }

  getComplaintLineStatusTypeValue(code: string) {
    const lookupTypeValues = this.complaintLineStatusType;
    const value = lookupTypeValues.find(item => item.lookupCode === code);
    return value ? value.lookupValue : code;
  }

  getUserComplaints(): void {
    this.complaintService.getUserComplaints().subscribe({
      next: (response) => {
        if (response) {
          // console.log(response);
          this.userComplaints = response;
          this.dataSource = new MatTableDataSource(this.userComplaints);
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
        }
      },
      error: (error) => {
        console.error('GET error:', error);
      },
    });
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }
}
